import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trash2, TrendingDown, DollarSign, Calendar, Home } from 'lucide-react';
import { motion } from 'framer-motion';
import type { ScenarioInput, ScenarioAnalysis, ComparisonMetrics } from '@/types/supremeIntelligenceTypes';

interface ScenarioCardProps {
  scenario: ScenarioInput;
  analysis: ScenarioAnalysis;
  metrics: ComparisonMetrics;
  onUpdate: (scenario: ScenarioInput) => void;
  onRemove: () => void;
  canRemove: boolean;
  index: number;
}

export const ScenarioCard: React.FC<ScenarioCardProps> = ({
  scenario,
  analysis,
  metrics,
  onUpdate,
  onRemove,
  canRemove,
  index,
}) => {
  const handleChange = (field: keyof ScenarioInput, value: string | number) => {
    onUpdate({ ...scenario, [field]: value });
  };

  const isBest = (metricKey: keyof ComparisonMetrics) => metrics[metricKey] === scenario.id;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card className="relative">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <Input
              value={scenario.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="font-semibold text-lg border-none p-0 h-auto focus-visible:ring-0"
              placeholder="Scenario Name"
            />
            {canRemove && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onRemove}
                className="h-8 w-8 text-destructive hover:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          {/* Best Badges */}
          <div className="flex flex-wrap gap-2 mt-2">
            {isBest('bestMonthlyPayment') && (
              <Badge variant="outline" className="text-chart-2 border-chart-2">
                Lowest Payment
              </Badge>
            )}
            {isBest('bestTotalCost') && (
              <Badge variant="outline" className="text-chart-3 border-chart-3">
                Lowest Total Cost
              </Badge>
            )}
            {isBest('bestAffordability') && (
              <Badge variant="outline" className="text-primary border-primary">
                Most Affordable
              </Badge>
            )}
            {isBest('lowestDTI') && (
              <Badge variant="outline" className="text-chart-4 border-chart-4">
                Best DTI
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Inputs */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor={`price-${scenario.id}`} className="text-xs">Home Price</Label>
              <Input
                id={`price-${scenario.id}`}
                type="number"
                value={scenario.homePrice}
                onChange={(e) => handleChange('homePrice', parseFloat(e.target.value) || 0)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor={`down-${scenario.id}`} className="text-xs">Down Payment %</Label>
              <Input
                id={`down-${scenario.id}`}
                type="number"
                value={scenario.downPaymentPercentage}
                onChange={(e) => handleChange('downPaymentPercentage', parseFloat(e.target.value) || 0)}
                min={0}
                max={100}
                step={0.5}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor={`rate-${scenario.id}`} className="text-xs">Interest Rate %</Label>
              <Input
                id={`rate-${scenario.id}`}
                type="number"
                value={scenario.interestRate}
                onChange={(e) => handleChange('interestRate', parseFloat(e.target.value) || 0)}
                step={0.125}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor={`term-${scenario.id}`} className="text-xs">Loan Term (years)</Label>
              <Input
                id={`term-${scenario.id}`}
                type="number"
                value={scenario.loanTerm}
                onChange={(e) => handleChange('loanTerm', parseInt(e.target.value) || 30)}
                className="mt-1"
              />
            </div>
          </div>

          {/* Results */}
          <div className="space-y-3 pt-3 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                Monthly Payment
              </span>
              <span className="font-semibold">{formatCurrency(analysis.monthlyPayment.total)}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <TrendingDown className="w-4 h-4" />
                Total Cost
              </span>
              <span className="font-semibold">{formatCurrency(analysis.totalCost30Year)}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Home className="w-4 h-4" />
                Affordability Score
              </span>
              <span className="font-semibold">{analysis.affordabilityScore}/100</span>
            </div>

            {analysis.dtiRatio > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  DTI Ratio
                </span>
                <span className="font-semibold">{analysis.dtiRatio.toFixed(1)}%</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
